import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { Bold, Italic, Link as LinkIcon, List, Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { CannedResponsePicker, useCannedResponseShortcut } from './CannedResponsePicker';
import { MentionDropdown, useMentionAutocomplete } from './MentionPicker';
import type { Attachment } from './FileAttachments';
import { ATTACHMENT_ACCEPT } from './attachmentPolicy';
import { sanitizeHtml } from '@centraoneplus/ui';
import { RecipientInput } from './RecipientInput';
import { getRecipientError, normalizeAddress } from './replyRecipients';

export interface ReplyRecipients {
  to: string[];
  cc: string[];
}

interface ReplyEditorProps {
  /**
   * Fixed by the parent — reply lives on Conversation, note on Notes, and the
   * editor can never switch between them. That is what makes "internal note
   * accidentally sent to the customer" structurally impossible.
   */
  mode: 'reply' | 'note';
  /** Discard — the parent collapses the dock. */
  onCancel?: () => void;
  /** Reply mode only: the suggestions strip, rendered above the shell. */
  suggestions?: ReactNode;
  /**
   * `mentions` carries teammates @mentioned in an internal note (note mode only).
   * It is passed only when at least one mention survives into the sent text, so
   * callers that ignore it keep their two-argument contract.
   */
  onSend: (
    content: string,
    isInternal: boolean,
    mentions?: Array<{ userId: string; userName: string }>,
    recipients?: ReplyRecipients,
  ) => Promise<void>;
  isSending?: boolean;
  disabled?: boolean;
  placeholder?: string;
  // Context for canned response variables
  customerName?: string;
  ticketNumber?: string;
  agentName?: string;
  /**
   * Where a public reply goes. Read-only text in the mode row, or — with
   * `editableRecipients` — the initial To address.
   */
  recipient?: string;
  /**
   * Reply mode only: show editable To + Cc rows. The chosen recipients are
   * passed to `onSend` as its fourth argument. Off by default, so callers that
   * don't opt in keep the read-only recipient line.
   */
  editableRecipients?: boolean;
  /**
   * With `editableRecipients`: the rows start empty and may stay empty (a
   * non-email ticket, whose reply goes out on its own channel). Addresses the
   * agent adds get an email copy. Default false: To starts with `recipient`
   * and at least one To is required (an email ticket).
   */
  recipientsOptional?: boolean;
  /** Who the reply is addressed to; personalises the placeholder. */
  recipientName?: string;
  /** Renders the footer's "Save draft" action. Omitted → no button. */
  onSaveDraft?: (content: string) => void;
  /** Renders the footer's "KB article" action. Omitted → no button. */
  onInsertKbArticle?: () => void;
  // Controlled mode props (for AI suggestions integration)
  initialContent?: string;
  onContentChange?: (content: string) => void;
  // Attachment upload (paperclip). When omitted, the button is hidden.
  onAttachFiles?: (files: File[]) => void | Promise<void>;
  isUploadingAttachment?: boolean;
  // Staged attachments to show as removable chips (sent with the next reply).
  attachments?: Attachment[];
  onRemoveAttachment?: (id: string) => void;
  /**
   * With `editableRecipients`: To/Cc to put back into the rows (an undone reply
   * being restored). Applied when it changes; `onRecipientsRestored` then lets
   * the parent clear it so a later reopen starts from the defaults again.
   */
  restoreRecipients?: ReplyRecipients | null;
  onRecipientsRestored?: () => void;
}

/** True when an HTML string contains visible text (ignoring tags / &nbsp;). */
function hasVisibleText(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim().length > 0
  );
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function looksLikeHtml(text: string): boolean {
  return /<[a-z!/][\s\S]*>/i.test(text);
}

/** Convert plain text (e.g. an AI suggestion or canned response) into safe HTML. */
function textToHtml(text: string): string {
  if (!text) return '';
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/**
 * Strip anything unsafe before we render or send editor HTML: scripts, embeds,
 * inline event handlers, and javascript: URLs. The rendered conversation thread
 * sanitizes again on its side, but we keep what leaves the composer clean too.
 */

/** Imperative handle for the parent to focus or clear the editor. Mode is a
 *  prop now, so there is no "switch to internal note" action to expose. */
export interface ReplyEditorHandle {
  focus: () => void;
  clear: () => void;
}

export const ReplyEditor = forwardRef<ReplyEditorHandle, ReplyEditorProps>(function ReplyEditor({
  mode,
  onCancel,
  suggestions,
  onSend,
  isSending = false,
  disabled = false,
  placeholder = 'Type your reply...',
  customerName,
  ticketNumber = '',
  agentName = 'Support Agent',
  recipient,
  editableRecipients = false,
  recipientsOptional = false,
  recipientName,
  onSaveDraft,
  onInsertKbArticle,
  initialContent,
  onContentChange,
  onAttachFiles,
  isUploadingAttachment = false,
  attachments = [],
  onRemoveAttachment,
  restoreRecipients,
  onRecipientsRestored,
}, ref) {
  const toast = useToast();
  // Only the setter is used — the editor content lives in the contentEditable
  // DOM; React state just mirrors it out to the parent via onContentChange.
  const [, setContentInternal] = useState(initialContent || '');
  const [isEmpty, setIsEmpty] = useState(!hasVisibleText(initialContent || ''));
  const isNote = mode === 'note';
  const editorRef = useRef<HTMLDivElement>(null);
  const cannedResponseButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks the last HTML we pushed out of the editor (via onContentChange) so the
  // sync effect can tell an echo of our own value apart from a genuine external
  // push. Without this, round-tripped innerHTML that lacks tags (e.g. "hi&nbsp;"
  // after typing a space) gets re-escaped as plain text on every keystroke,
  // turning & into &amp; → &amp;amp; → … indefinitely.
  const lastSyncedHtml = useRef<string | undefined>(undefined);

  // To / Cc. On an email ticket each reply starts addressed to the ticket
  // contact with no Cc; the agent can add or remove addresses from there. On
  // any other ticket both start empty — the reply goes out on its own channel,
  // and only addresses the agent adds get an email copy.
  const showRecipients = !isNote && editableRecipients;
  const defaultTo = useMemo(
    () => (recipient && !recipientsOptional ? [normalizeAddress(recipient)] : []),
    [recipient, recipientsOptional],
  );
  const [to, setTo] = useState<string[]>(defaultTo);
  const [cc, setCc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  // The ticket (and so the contact) can load after the composer mounts.
  useEffect(() => {
    setTo(defaultTo);
  }, [defaultTo]);
  const resetRecipients = useCallback(() => {
    setTo(defaultTo);
    setCc([]);
    setShowCc(false);
  }, [defaultTo]);
  // Declared after the defaultTo sync above so a restore wins on mount.
  useEffect(() => {
    if (!restoreRecipients || !showRecipients) return;
    setTo(restoreRecipients.to.length > 0 || recipientsOptional ? restoreRecipients.to : defaultTo);
    setCc(restoreRecipients.cc);
    setShowCc(restoreRecipients.cc.length > 0);
    onRecipientsRestored?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreRecipients]);

  // Wrapper to notify parent of content changes (HTML).
  const setContent = useCallback(
    (html: string) => {
      setContentInternal(html);
      onContentChange?.(html);
    },
    [onContentChange]
  );

  // Let a parent switch the composer into internal-note mode and focus it (used
  // by the Notes tab's "create a new note" action). Scrolls into view since the
  // composer sits at the bottom of the ticket's scroll area.
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editorRef.current?.focus(),
      clear: () => {
        if (editorRef.current) editorRef.current.innerHTML = '';
        lastSyncedHtml.current = '';
        setContent('');
        setIsEmpty(true);
      },
    }),
    [setContent],
  );

  // Opening the editor should put the caret in it — the dock just expanded
  // because the user asked to write.
  useEffect(() => {
    editorRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the live editor DOM back into React state + parent.
  const syncFromDom = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastSyncedHtml.current = html;
    setContent(html);
    setIsEmpty((el.textContent || '').trim() === '' && !el.querySelector('img'));
  }, [setContent]);

  // Keep the contentEditable surface in sync when the parent pushes new content
  // (AI suggestions, canned-response shortcuts cleared, etc.). We compare against
  // the live innerHTML so normal typing — which already matches — never resets the
  // caret, and we mirror the normalized HTML back up so the values converge.
  useEffect(() => {
    if (initialContent === undefined) return;
    const el = editorRef.current;
    if (!el) return;
    // Skip values the editor itself just emitted — they're already HTML and
    // must not be re-run through textToHtml (which would escape entities and
    // double-encode on every keystroke).
    if (initialContent === lastSyncedHtml.current) return;
    const html = looksLikeHtml(initialContent)
      ? initialContent
      : textToHtml(initialContent);
    if (html === el.innerHTML) return;
    el.innerHTML = html;
    lastSyncedHtml.current = html;
    setContent(html);
    setIsEmpty((el.textContent || '').trim() === '' && !el.querySelector('img'));
  }, [initialContent, setContent]);

  // Variables for canned response substitution
  const variables = {
    customer_name: customerName || 'Customer',
    ticket_number: ticketNumber,
    agent_name: agentName,
  };

  // Handle canned response insertion at the caret.
  const handleInsertCannedResponse = useCallback(
    (responseContent: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      document.execCommand('insertText', false, responseContent);
      syncFromDom();
    },
    [syncFromDom]
  );

  // Hook for shortcut detection (e.g., /thanks) + Ctrl/Cmd+/ toggle.
  const { showPicker, setShowPicker } = useCannedResponseShortcut(
    editorRef,
    syncFromDom,
    variables
  );

  // #18935 — @mention autocomplete, in notes and replies. Typing @ lists the
  // organisation's teammates; a picked teammate gets an in-app mention
  // notification (in a reply they are NOT added to the email). The teammate
  // list also feeds the To/Cc suggestions.
  const mention = useMentionAutocomplete(editorRef, syncFromDom, true);

  const handleSubmit = useCallback(async () => {
    if (isSending || disabled) return;
    const el = editorRef.current;
    const text = (el?.textContent || '').trim();
    // A reply must contain text — an attachment on its own isn't enough (the
    // backend rejects empty content). Tell the agent clearly instead of letting
    // it fail with a raw 400.
    if (!text) {
      toast.error('Message cannot be empty', 'Please type a message before sending.');
      return;
    }

    // Nothing is sent while any recipient is missing or malformed.
    if (showRecipients) {
      const recipientError = getRecipientError(to, cc, { requireTo: !recipientsOptional });
      if (recipientError) {
        toast.error('Check the recipients', recipientError);
        return;
      }
    }

    const html = sanitizeHtml(el?.innerHTML || '');
    // Only teammates whose @name is still present in the text are sent, so a
    // removed mention sends no notification. Pass the third arg only when there
    // is something to send, keeping the two-argument contract otherwise.
    const mentions = mention.collectMentions(text);
    try {
      if (showRecipients && (to.length > 0 || cc.length > 0)) {
        await onSend(html, false, mentions.length > 0 ? mentions : undefined, { to, cc });
      } else if (mentions.length > 0) {
        await onSend(html, isNote, mentions);
      } else {
        await onSend(html, isNote);
      }
      if (editorRef.current) editorRef.current.innerHTML = '';
      lastSyncedHtml.current = '';
      setContent('');
      setIsEmpty(true);
      mention.reset();
      if (showRecipients) resetRecipients();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(
        'Failed to send',
        error?.response?.data?.message || error?.message || 'Could not send your message. Please try again.',
      );
    }
  }, [isSending, disabled, onSend, isNote, setContent, toast, mention, showRecipients, recipientsOptional, to, cc, resetRecipients]);

  /** Clear the composer and collapse the dock. Local only. */
  const handleDiscard = useCallback(() => {
    if (editorRef.current) editorRef.current.innerHTML = '';
    lastSyncedHtml.current = '';
    setContent('');
    setIsEmpty(true);
    resetRecipients();
    onCancel?.();
  }, [setContent, onCancel, resetRecipients]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // Apply a rich-text command to the current selection. Buttons preventDefault
  // on mousedown so the editor keeps focus/selection while we run the command.
  const exec = useCallback(
    (command: string, value?: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      document.execCommand(command, false, value);
      syncFromDom();
    },
    [syncFromDom]
  );

  const handleLink = useCallback(() => {
    const url = window.prompt('Enter the link URL:');
    if (url) exec('createLink', url);
  }, [exec]);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      // Reset so selecting the same file again still fires onChange.
      e.target.value = '';
      if (files.length > 0) {
        await onAttachFiles?.(files);
      }
    },
    [onAttachFiles]
  );

  const formattingDisabled = disabled || isSending;

  const formatButton = (
    title: string,
    Icon: typeof Bold,
    onClick: () => void,
  ) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={formattingDisabled}
      className="p-[5px] text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:text-[var(--co-brass-ink)] disabled:cursor-not-allowed disabled:opacity-40"
      title={title}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  // Only personalise the placeholder when the caller actually supplied a name —
  // otherwise the `placeholder` prop must survive verbatim.
  const firstName = (recipientName || customerName)?.trim().split(/\s+/)[0];
  const effectivePlaceholder = isNote
    ? 'Note for the team…'
    : firstName
      ? `Write a reply to ${firstName}…`
      : placeholder;

  return (
    <div className="space-y-[9px]">
      {/* Reply mode only — suggestions belong to the open editor, not the page. */}
      {!isNote && suggestions}

      {/* Brass border marks this as the active surface while the dock is open. */}
      <div className="border border-[var(--co-brass)] bg-[var(--co-raised)]">
      {/* Header — one fixed label. No tabs: the mode is the mount. */}
      <div className="flex items-center gap-3 border-b border-[var(--co-line-subtle)] px-[14px] py-[9px]">
        <span className="border-b-2 border-[var(--co-brass)] pb-[7px] font-mono text-[10.5px] font-semibold uppercase leading-none tracking-[.13em] text-[var(--co-ink)]">
          {isNote ? 'Internal note' : 'Reply'}
        </span>
        <span
          className={clsx(
            'ml-auto truncate font-mono text-[10.5px] uppercase leading-none tracking-[.1em]',
            isNote ? 'text-[var(--co-brass-ink)]' : 'text-[var(--co-ink-4)]',
          )}
        >
          {isNote
            ? 'Not visible to customer'
            : showRecipients
              ? recipientsOptional
                ? 'Email copy optional'
                : 'Email'
              : recipient
                ? `To ${recipient}`
                : 'Will be sent to the customer'}
        </span>
        {onCancel && (
          <span
            aria-hidden="true"
            className="hidden flex-none border border-[var(--co-line)] px-[5px] py-[2px] font-mono text-[9.5px] uppercase leading-none text-[var(--co-ink-4)] sm:inline-block"
          >
            Esc
          </span>
        )}
      </div>

      {/* Recipients (email replies) — To always, Cc on demand. */}
      {showRecipients && (
        <>
          <RecipientInput
            label="To"
            value={to}
            onChange={setTo}
            exclude={cc}
            suggestions={mention.users}
            disabled={disabled || isSending}
            placeholder={recipientsOptional ? 'Optional: also email this reply to…' : 'Add recipients'}
            trailing={
              !showCc && (
                <button
                  type="button"
                  onClick={() => setShowCc(true)}
                  disabled={disabled || isSending}
                  className="co-action flex-none pt-[4px] disabled:opacity-40"
                >
                  Cc
                </button>
              )
            }
          />
          {showCc && (
            <RecipientInput
              label="Cc"
              value={cc}
              onChange={setCc}
              exclude={to}
              suggestions={mention.users}
              disabled={disabled || isSending}
              placeholder="Add Cc recipients"
            />
          )}
        </>
      )}

      {/* Formatting toolbar */}
      <div className="flex items-center gap-1 border-b border-[var(--co-line-subtle)] px-[9px] py-[5px]">
        {formatButton('Bold', Bold, () => exec('bold'))}
        {formatButton('Italic', Italic, () => exec('italic'))}
        {formatButton('Link', LinkIcon, handleLink)}
        {formatButton('List', List, () => exec('insertUnorderedList'))}

        <span aria-hidden="true" className="mx-1 h-4 w-px bg-[var(--co-line)]" />

        {onAttachFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={handleFilesSelected}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleAttachClick}
              disabled={disabled || isSending || isUploadingAttachment}
              className={clsx(
                'p-[5px] text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:text-[var(--co-brass-ink)] disabled:cursor-not-allowed disabled:opacity-40',
                isUploadingAttachment && 'animate-pulse',
              )}
              title="Attach file"
              aria-label="Attach file"
            >
              <Paperclip className="h-4 w-4" />
            </button>
          </>
        )}
        <div className="relative">
          <button
            ref={cannedResponseButtonRef}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowPicker(!showPicker)}
            className={clsx('co-action ml-1', showPicker && 'text-[var(--co-brass-ink)]')}
            title="Canned responses (Ctrl+/)"
          >
            Macro
          </button>
          <CannedResponsePicker
            isOpen={showPicker}
            onClose={() => setShowPicker(false)}
            onSelect={handleInsertCannedResponse}
            variables={variables}
            triggerRef={cannedResponseButtonRef}
          />
        </div>
        {!isNote && onInsertKbArticle && (
          <button
            type="button"
            onClick={onInsertKbArticle}
            disabled={disabled || isSending}
            className="co-action ml-2 disabled:opacity-40"
          >
            KB article
          </button>
        )}
      </div>

      {/* Content area */}
      <div className="relative">
        {/* Rich-text editor — always mounted so its DOM content survives a
            preview toggle. Content is managed imperatively (no React children). */}
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-label={effectivePlaceholder}
          contentEditable={!disabled && !isSending}
          suppressContentEditableWarning
          onInput={syncFromDom}
          onKeyDown={handleKeyDown}
          className={clsx(
            'min-h-[70px] w-full overflow-y-auto px-[15px] py-[13px] text-[13.5px] leading-[1.6] text-[var(--co-ink)] focus:outline-none',
            isNote && 'bg-[var(--co-brass-wash)]',
            '[&_ul]:ml-5 [&_ul]:list-disc [&_ol]:ml-5 [&_ol]:list-decimal',
            '[&_a]:text-[var(--co-brass-ink)] [&_a]:underline',
          )}
        />

        {/* Placeholder overlay (contentEditable has no native placeholder) */}
        {isEmpty && (
          <div className="pointer-events-none absolute left-0 top-0 px-[15px] py-[13px] text-[13.5px] leading-[1.62] text-[var(--co-ink-4)]">
            {effectivePlaceholder}
          </div>
        )}

        {/* @mention autocomplete (internal notes only) */}
        <MentionDropdown
          open={mention.open}
          items={mention.items}
          activeIndex={mention.activeIndex}
          onHover={mention.setActiveIndex}
          onSelect={mention.insert}
        />
      </div>

      {/* Staged attachments (sent with the reply) */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-[13px] pt-2">
          {attachments.map((att) => {
            const isImage = att.mimeType?.startsWith('image/');
            // Note: we intentionally don't render <img src={att.url}> here — the
            // download URL needs an auth header a plain <img> can't send, so it
            // would show a broken image. A clear file-type icon is reliable.
            const Icon = isImage ? ImageIcon : FileText;
            return (
              <div
                key={att.id}
                className="co-tile flex max-w-[220px] items-center gap-2 !px-2 !py-1"
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0 text-[var(--co-ink-4)]" />
                <span
                  className="truncate font-mono text-[10.5px] text-[var(--co-ink-2)]"
                  title={att.name}
                >
                  {att.name}
                </span>
                {onRemoveAttachment && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(att.id)}
                    disabled={disabled || isSending}
                    className="flex h-4 w-4 flex-none items-center justify-center text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:text-[var(--co-burgundy)] disabled:opacity-40"
                    title="Remove attachment"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-4 border-t border-[var(--co-line-subtle)] px-[13px] py-[10px]">
        <div className="ml-auto flex flex-none items-center gap-2">
          <span className="mr-1 hidden font-mono text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-4)] sm:inline">
            {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} + Enter to send
          </span>

          {!isNote && onSaveDraft && (
            <button
              type="button"
              onClick={() => onSaveDraft(editorRef.current?.innerHTML || '')}
              disabled={isEmpty || isSending || disabled}
              className="btn btn-outline h-8 px-3 text-[12.5px] disabled:opacity-50"
            >
              Save draft
            </button>
          )}

          <button
            type="button"
            onClick={handleDiscard}
            disabled={isSending}
            className="btn btn-outline h-8 px-3 text-[12.5px] disabled:opacity-50"
          >
            Discard
          </button>

          {/* A note must not look like a send: ink fill, not brass. */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={(isEmpty && attachments.length === 0) || isSending || disabled}
            className={clsx(
              'btn h-8 px-3 text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50',
              isNote ? 'border border-[var(--co-ink)]' : 'btn-primary',
            )}
            style={
              isNote ? { background: 'var(--co-ink)', color: 'var(--co-ink-invert)' } : undefined
            }
          >
            {isSending
              ? isNote
                ? 'Saving...'
                : 'Sending...'
              : isNote
                ? 'Save note'
                : 'Send reply'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
});

export default ReplyEditor;
